import { ofType } from 'redux-observable';
import { map, mergeMap, filter, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { callApiGetEquipment, callApiGetStatus, callApiPutEquipment } from './apiCalls';
import webApi from '../../../Lib/WebApi';
import { callApi, catchWithAuthErrors, addErrorNotice } from '../../Helpers/rxjsHelpers';
import { concatLatest, switchMapByKey } from '../../../Lib/Rxjs/operators';
import { config as equipmentConfig } from '../../../Components/Equipment';
import { SyncStatus, syncTarget } from '../../Core/legacyDataSync';
import { actions } from '.';

export const init = (action$) =>
  action$.pipe(
    ofType(actions.init.toString()),
    mergeMap(({ payload: { equipmentId } = {} }) =>
      callApi(webApi.equipment.type.get({ equipmentId })).pipe(
        map((type) => type.Body),
        mergeMap((type) =>
          callApiGetEquipment(type.EquipmentType, equipmentId, true).pipe(
            mergeMap((equipment) =>
              of(
                actions.initResolve({
                  equipmentType: type,
                  equipment,
                  config: type?.Id && equipmentConfig ? equipmentConfig[type.Id] : null,
                  sync: true,
                })
              )
            )
          )
        )
      )
    ),
    catchWithAuthErrors((err) => of(actions.initReject(), addErrorNotice('Failed to initialise equipment.', err)))
  );

export const syncOnChanges = (action$) =>
  action$.pipe(
    ofType(
      // After equipment is reloaded, run a sync to ensure things are in correct state.
      actions.initResolve.toString(),

      // On sync completion, run sync again to make sure new changes are synchronised
      actions.synchroniseComplete.toString(),

      // Whenever a value changes
      actions.setValue.toString(),
      actions.copyValue.toString(),
      actions.patchValues.toString(),
      actions.addArrayEntry.toString(),
      actions.removeArrayEntry.toString()
    ),
    filter(({ payload: { sync } = {} }) => !!sync), // only when sync is set to true
    mergeMap(() => of(actions.synchronise()))
  );

// This is our main synchronisation epic, that is responsible for keeping the server up-to-date with the clients changes.
// This runs like a service, continually (once at a time) updating the server state to look like the client state.
export const syncData = (action$, state$) =>
  action$.pipe(
    ofType(actions.synchronise.toString()),
    concatLatest(({ payload: { force } = {} }) =>
      syncTarget(state$.value.equipment, force, ({ updateModel }) => {
        const equipmentTypeName = state$.value.equipment.equipmentType.EquipmentType;
        const equipmentId = state$.value.equipment.equipmentId;
        return updateModel
          ? callApiPutEquipment(equipmentTypeName, { Body: updateModel }, { equipmentId })
          : callApiGetEquipment(equipmentTypeName, equipmentId);
      })
    ),
    mergeMap(({ status, syncPatch, error, recoveryError }) => {
      const epicActionsOut = [];
      if (error) epicActionsOut.push(addErrorNotice('Failed to update equipment.', error));
      if (recoveryError)
        epicActionsOut.push(
          addErrorNotice(
            'Something went really wrong while attempting to retrieve equipment, try refreshing the page.',
            recoveryError
          )
        );
      epicActionsOut.push(actions.synchroniseComplete({ sync: status === SyncStatus.success, syncPatch }));
      return of(...epicActionsOut);
    })
  );

export const persistTempState = (action$, state$) =>
  action$.pipe(
    ofType(actions.persistTempState.toString()),
    switchMap(({ payload: { tempId, name } = {} }) => {
      return of(
        actions.patchValues({
          name,
          keyValuePairs: state$.value.equipmentEditor.equipmentTemp[tempId],
          sync: true,
        }),
        actions.cleanupTempState({ tempId })
      );
    })
  );

export const getStatus = (action$, state$) =>
  action$.pipe(
    ofType(actions.getStatus.toString()),
    switchMapByKey(
      (action) => action.payload.id,
      ({ payload: { id } = {} }) => {
        return state$.value.equipment.statuses[id]?.initialised &&
          state$.value.equipment.statuses[id].equipmentId &&
          state$.value.equipment.statuses[id].statusDate
          ? callApiGetStatus(
              state$.value.equipment.statuses[id].equipmentId,
              state$.value.equipment.statuses[id].statusDate
            ).pipe(
              switchMap((result) =>
                of(
                  actions.getStatusResolve({
                    id,
                    result,
                  })
                )
              ),
              catchWithAuthErrors((err) =>
                of(actions.getStatusReject({ id }), addErrorNotice('Failed to retrieve equipment status', err))
              )
            )
          : of(actions.getStatusReject({ id }));
      }
    )
  );

export const getEquipmentData = (action$, state$) =>
  action$.pipe(
    ofType(actions.getEquipmentData.toString()),
    switchMapByKey(
      (action) => action.payload.id,
      ({ payload: { id } = {} }) => {
        return state$.value.equipment.equipmentData[id]?.initialised &&
          state$.value.equipment.equipmentData[id].equipmentId
          ? callApi(
              webApi.equipment.type.get({ equipmentId: state$.value.equipment.equipmentData[id].equipmentId })
            ).pipe(
              map((type) => type.Body),
              switchMap((type) =>
                callApiGetEquipment(type.EquipmentType, state$.value.equipment.equipmentData[id].equipmentId).pipe(
                  switchMap((equipment) =>
                    of(
                      actions.getEquipmentDataResolve({
                        id,
                        equipment,
                      })
                    )
                  ),
                  catchWithAuthErrors((err) =>
                    of(actions.getEquipmentDataReject({ id }), addErrorNotice('Failed to retrieve equipment data', err))
                  )
                )
              ),
              catchWithAuthErrors((err) =>
                of(actions.getEquipmentDataReject({ id }), addErrorNotice('Failed to retrieve equipment data', err))
              )
            )
          : of(actions.getEquipmentDataReject({ id }));
      }
    )
  );
